import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, object = authed, false = not authed

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from the Google OAuth callback, skip the /me
    // check - AuthCallback will exchange the session_id and set the auth
    // cookies first, otherwise this races and always sees "not logged in".
    if (window.location.hash?.includes("session_id=")) {
      return;
    }
    fetchMe();
  }, [fetchMe]);

  // Exchanges a one-time Emergent Auth session_id for the caller's Google
  // email, and only logs them in if that email already exists in `users`.
  const completeGoogleLogin = async (sessionId) => {
    try {
      const { data } = await api.post("/auth/google/session", {}, { headers: { "X-Session-ID": sessionId } });
      setUser(data);
      return { success: true, user: data };
    } catch (e) {
      setUser(false);
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.error("Logout request failed:", err);
    }
    setUser(false);
  };

  return <AuthContext.Provider value={{ user, completeGoogleLogin, logout, refetch: fetchMe }}>{children}</AuthContext.Provider>;
}

export function getDefaultRouteForRole(role) {
  return role === "inventory_manager" ? "/admin/inventory" : "/dashboard";
}

export function useAuth() {
  return useContext(AuthContext);
}
